import { NavLink } from "react-router-dom";
import { Monitor, ImagePlus, AlertTriangle, User, Rewind } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Bottom tab bar for mobile viewports. Thumb-friendly, Instagram/Twitter style
 * navigation. Hidden on md+ screens (desktop uses the top header).
 * Shows an unread badge on Alerts when new motion events land since last visit.
 */
export default function MobileBottomNav({ active }) {
  const { user } = useAuth();
  const [alertCount, setAlertCount] = useState(0);
  const isSuper = user?.role === "super_admin";

  // Poll motion alerts and show a badge only for events newer than the user's
  // last visit to the /alerts route (persisted in localStorage).
  useEffect(() => {
    if (isSuper) return;
    let mounted = true;
    const compute = (events) => {
      const ts = parseInt(localStorage.getItem("cctv_alerts_last_viewed_at") || "0", 10);
      const fresh = (events || []).filter((e) => new Date(e.created_at || e.timestamp).getTime() > ts);
      setAlertCount(fresh.length);
    };
    let latest = [];
    const load = () =>
      api.get("/motion-alerts?limit=30")
        .then((r) => {
          if (!mounted) return;
          latest = r.data || [];
          compute(latest);
        })
        .catch(() => {});
    load();
    const t = setInterval(load, 15000);
    // Cross-tab sync: if user reads alerts in another tab, this tab's badge clears too.
    const onStorage = (e) => {
      if (e.key === "cctv_alerts_last_viewed_at") compute(latest);
    };
    window.addEventListener("storage", onStorage);
    return () => { mounted = false; clearInterval(t); window.removeEventListener("storage", onStorage); };
  }, [isSuper]);

  if (isSuper) return null; // super admin has no live view / gallery / alerts

  const tabs = [
    { to: "/dashboard", label: "Live", icon: Monitor, testid: "mobilenav-live", key: "dashboard" },
    { to: "/playback",  label: "Playback", icon: Rewind, testid: "mobilenav-playback", key: "playback" },
    { to: "/gallery",   label: "Gallery", icon: ImagePlus, testid: "mobilenav-gallery", key: "gallery" },
    { to: "/alerts",    label: "Alerts", icon: AlertTriangle, testid: "mobilenav-alerts", key: "alerts", badge: alertCount },
    { to: "/admin",     label: "Admin", icon: User, testid: "mobilenav-admin", key: "admin" },
  ];

  return (
    <nav
      data-testid="mobile-bottom-nav"
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-zinc-950 border-t border-zinc-800 flex items-stretch"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
    >
      {tabs.map((t) => {
        const Icon = t.icon;
        const isActive = active === t.key;
        return (
          <NavLink
            key={t.key}
            to={t.to}
            data-testid={t.testid}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 mono uppercase text-[9px] tracking-widest relative ${isActive ? "text-cyan-400 bg-zinc-900" : "text-zinc-500"}`}
          >
            <Icon size={18} />
            <span>{t.label}</span>
            {t.badge > 0 && (
              <span
                data-testid={`${t.testid}-badge`}
                className="absolute top-1 right-1/2 translate-x-4 min-w-[16px] h-[16px] px-1 text-[8px] mono flex items-center justify-center bg-red-500 text-white rounded-full"
              >
                {t.badge > 99 ? "99+" : t.badge}
              </span>
            )}
            {isActive && (
              <span className="absolute top-0 inset-x-4 h-[2px] bg-cyan-400" />
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}
